import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
interface AudioPlayerProps {
  src: string;
}
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progressPercentage = duration > 0 ? currentTime / duration * 100 : 0;
  return <div className="w-full" data-id="element-20">
      <audio ref={audioRef} src={src} preload="metadata" data-id="element-21" />
      <div className="flex items-center gap-4" data-id="element-22">
        {/* Play/Pause Button */}
        <button onClick={togglePlay} className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-bttf-primary to-bttf-primary-hover flex items-center justify-center shadow-[0_0_15px_rgba(255,108,0,0.5)] hover:shadow-[0_0_20px_rgba(255,211,0,0.7)] transition-all duration-300 hover:scale-105" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-23">
          {isPlaying ? <PauseIcon className="h-6 w-6 text-bttf-text" data-id="element-24" /> : <PlayIcon className="h-6 w-6 text-bttf-text ml-1" data-id="element-25" />}
        </button>
        {/* Time Display */}
        <div className="flex-shrink-0 text-sm font-mono text-bttf-accent font-semibold min-w-[80px]" data-id="element-26">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
        {/* Progress Bar */}
        <div className="flex-1 relative group" data-id="element-27">
          <div className="h-2 bg-bttf-bg/60 rounded-full overflow-hidden border border-bttf-accent/20" data-id="element-28">
            <div className="h-full bg-gradient-to-r from-bttf-primary via-bttf-primary-hover to-bttf-accent transition-all duration-150 shadow-[0_0_10px_rgba(255,108,0,0.5)]" style={{
            width: `${progressPercentage}%`
          }} data-id="element-29" />
          </div>
          <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Seek audio" data-id="element-30" />
        </div>
        {/* Volume Control */}
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-31">
          <button onClick={toggleMute} className="w-8 h-8 rounded-full bg-bttf-bg/60 border border-bttf-accent/30 flex items-center justify-center hover:bg-bttf-accent/20 transition-all duration-300" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-32">
            {isMuted ? <VolumeXIcon className="h-4 w-4 text-bttf-accent" data-id="element-33" /> : <Volume2Icon className="h-4 w-4 text-bttf-accent" data-id="element-34" />}
          </button>
          <div className="w-20 relative group/volume" data-id="element-35">
            <div className="h-1.5 bg-bttf-bg/60 rounded-full overflow-hidden border border-bttf-accent/20" data-id="element-36">
              <div className="h-full bg-gradient-to-r from-bttf-accent to-bttf-secondary transition-all duration-150" style={{
              width: `${(isMuted ? 0 : volume) * 100}%`
            }} data-id="element-37" />
            </div>
            <input type="range" min="0" max="1" step="0.01" value={isMuted ? 0 : volume} onChange={handleVolumeChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Volume" data-id="element-38" />
          </div>
        </div>
      </div>
    </div>;
};